/**
 * Page AST → reactive cells (ADR-0043 F1 · slice 5).
 *
 * The bridge between the existing page model (`lib/pageTypes.ts`) and the
 * reactive engine (slice 3). A pure transform: walk a `PageAst` and emit the
 * `EngineCell[]` that, fed to `createReactiveEngine`, drives the page reactively.
 * This is deliberately SEPARATE from `PageRenderer` — wiring the engine into the
 * actual render path (and firing query refetches on `stale`) is the next slice;
 * here we only produce (and test) the cell model, so the integration lands on a
 * proven foundation.
 *
 * The mapping:
 *  - **page variable** `v` → an EXTERNAL cell producing `$page.<v.name>` (its
 *    value is seeded + written by inputs; the engine propagates through it).
 *  - **query** `q` → an EXTERNAL cell producing `$queries.<q.name>`, whose deps
 *    are the paths its templated params read (filters / inputs / http url+body…)
 *    PLUS its explicit `q.deps` page-var names. So when a dep changes the engine
 *    reports the query `stale` and the integration layer refetches it.
 *  - **node prop** that is a `{{ }}` template → a DERIVED cell producing
 *    `$cells.<nodeId>.<propKey>`; `visible_when` → `$cells.<nodeId>.$visible`.
 *    The renderer reads a node's computed props from `$cells` instead of
 *    re-evaluating them itself.
 */

import { extractTemplateDeps, type DepPath } from "./deps";
import type { EngineCell } from "./engine";
import type { PageAst, PageNode, PageQuery } from "../pageTypes";

const isTemplate = (v: unknown): v is string => typeof v === "string" && v.includes("{{");

/** Recursively collect every string leaf under `value` (for dep scanning). */
function collectStrings(value: unknown, out: string[]): void {
  if (typeof value === "string") {
    out.push(value);
  } else if (Array.isArray(value)) {
    for (const el of value) collectStrings(el, out);
  } else if (value && typeof value === "object") {
    for (const v of Object.values(value)) collectStrings(v, out);
  }
}

/** The dep paths a query's templated params read, plus its declared `deps`. */
function queryDeps(q: PageQuery): DepPath[] {
  const out: string[][] = [];
  // Explicit reactive deps are page-var names (ADR-0022 Phase C).
  for (const name of q.deps ?? []) out.push(["$page", name]);
  // Templated params: filters/url/headers/query/body may carry `{{ }}`.
  const strings: string[] = [];
  collectStrings(q.filters, strings);
  collectStrings(q.url, strings);
  collectStrings(q.headers, strings);
  collectStrings(q.query, strings);
  collectStrings(q.body, strings);
  for (const s of strings) {
    for (const p of extractTemplateDeps(s)) out.push([...p]);
  }
  return out;
}

/** Walk the node tree, emitting a derived cell per `{{ }}` prop + visible_when. */
function walkNode(node: PageNode, out: EngineCell[]): void {
  for (const [key, val] of Object.entries(node.props ?? {})) {
    if (isTemplate(val)) {
      out.push({
        id: `prop:${node.id}:${key}`,
        source: val,
        produces: ["$cells", node.id, key],
      });
    }
  }
  if (isTemplate(node.visible_when)) {
    out.push({
      id: `vis:${node.id}`,
      source: node.visible_when,
      produces: ["$cells", node.id, "$visible"],
    });
  }
  for (const child of node.children ?? []) walkNode(child, out);
}

/** Translate a whole page into engine cells. Pure + total. */
export function pageToCells(ast: PageAst): EngineCell[] {
  const cells: EngineCell[] = [];
  for (const v of ast.variables ?? []) {
    cells.push({ id: `var:${v.name}`, external: true, deps: [], produces: ["$page", v.name] });
  }
  for (const q of ast.queries ?? []) {
    cells.push({
      id: `query:${q.name}`,
      external: true,
      deps: queryDeps(q),
      produces: ["$queries", q.name],
    });
  }
  if (ast.root) walkNode(ast.root, cells);
  return cells;
}

/** The engine's initial scope for a page: seeded page vars + an empty `$queries`. */
export function pageInitialState(ast: PageAst): Record<string, unknown> {
  const page: Record<string, unknown> = {};
  for (const v of ast.variables ?? []) page[v.name] = v.initial ?? null;
  return { $page: page, $queries: {}, $cells: {} };
}
